import axios from 'axios';
import type { PaymentMethod } from '../components/PaymentMethodSwitch';

/**
 * #512: cupom restrito por forma de pagamento. Espelho de
 * `backend/src/coupon/coupon-restrictions.ts` — quem decide é o servidor, na
 * criação do pagamento; aqui é só o preço e o aviso por forma de pagamento.
 * O tipo de plano já é recusado no `POST /coupons/validate` (com `planId`).
 */
export type CouponRestrito = {
  code: string;
  allowed_payment_method?: PaymentMethod | null;
};

const NOME_DA_FORMA: Record<PaymentMethod, string> = {
  pix: 'PIX',
  'credit-card': 'cartão de crédito',
};

/** O cupom vale nesta forma? Cupom sem restrição (todos os antigos) vale em todas. */
export function cupomValeNaForma(cupom: CouponRestrito | null | undefined, forma: PaymentMethod): boolean {
  return !cupom?.allowed_payment_method || cupom.allowed_payment_method === forma;
}

/**
 * O cupom que vai pro preço e pro POST desta forma: o aplicado, ou null quando
 * ele não vale aqui (a aba mostra o preço cheio com o aviso abaixo, e o POST
 * sai sem cupom em vez de ser recusado pelo servidor).
 */
export function cupomDaForma<T extends CouponRestrito>(cupom: T | null | undefined, forma: PaymentMethod): T | null {
  return cupom && cupomValeNaForma(cupom, forma) ? cupom : null;
}

/** Aviso da aba em que o cupom aplicado não vale, ou null. */
export function avisoCupomForaDaForma(cupom: CouponRestrito | null | undefined, forma: PaymentMethod): string | null {
  if (!cupom || cupomValeNaForma(cupom, forma)) return null;
  const outra = NOME_DA_FORMA[cupom.allowed_payment_method as PaymentMethod];
  return `O cupom ${cupom.code} vale só no ${outra}. No ${NOME_DA_FORMA[forma]} o valor é o preço cheio.`;
}

/**
 * Cupom que só vale numa forma que este plano nem oferece (ex.: só PIX num
 * plano anual do checkout público, que é só cartão): é inutilizável, então a
 * validação recusa com esta mensagem em vez de aceitar.
 */
export function cupomSemFormaDisponivel(
  cupom: CouponRestrito | null | undefined,
  formasDisponiveis: PaymentMethod[],
): string | null {
  const forma = cupom?.allowed_payment_method;
  if (!forma || formasDisponiveis.includes(forma)) return null;
  return `Este cupom vale só no ${NOME_DA_FORMA[forma]}, que não está disponível para este plano.`;
}

/** Mensagem do backend na recusa do cupom (plano não aceito, expirado...), ou a genérica. */
export function mensagemErroCupom(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const msg = (error.response?.data as { message?: string | string[] } | undefined)?.message;
    if (Array.isArray(msg) && msg.length) return msg[0];
    if (typeof msg === 'string' && msg) return msg;
  }
  return 'Cupom inválido ou expirado.';
}
